import { FlexRow } from '@/components/common/Flex';
import { MotionBox } from '@/components/common/Motion';
import { FlexProps, Tooltip } from '@chakra-ui/react';
import { PartnerSwitcherType } from '@common/react/partnerships';
import { toPx } from '@common/util/styles';
import { MotionProps, Variants } from 'framer-motion';
import { FC } from 'react';
import { SharedBlend } from './icons';
import PartnerAvatar from './PartnerAvatar';
import { hoverStyleProps } from './styleHelpers';
import UserAvatar from './UserAvatar';

const HEIGHT = 50;
const PADDING_X = 2;
const PADDING_Y = 2;
const OPTION_WIDTH = 73;
const WIDTH = PADDING_X * 2 + OPTION_WIDTH * 3;
const PILL_HEIGHT = HEIGHT - PADDING_Y * 2;

const variants: Variants = {
    MINE: { x: PADDING_X },
    SHARED: { x: PADDING_X + OPTION_WIDTH },
    THEIRS: { x: PADDING_X + OPTION_WIDTH * 2 },
};

export type PartnerSwitcherCustomOptions = {
    MINE?: {
        label?: string;
        disabled?: boolean;
    };
    SHARED?: {
        label?: string;
        disabled?: boolean;
    };
    THEIRS?: {
        label?: string;
        disabled?: boolean;
    };
};

interface PartnerSwitcherProps extends Omit<FlexProps, 'transition'> {
    value: PartnerSwitcherType;
    onChanged?: (value: PartnerSwitcherType) => void;
    transition?: MotionProps['transition'];
    customOptions?: PartnerSwitcherCustomOptions;
}

const PartnerSwitcher: FC<PartnerSwitcherProps> = ({
    value,
    onChanged,
    transition,
    customOptions,
    ...props
}) => {
    const options: {
        value: PartnerSwitcherType;
        el: React.ReactNode;
        tooltip: string;
    }[] = [
        {
            value: 'MINE',
            tooltip: 'Yours',
            el: <UserAvatar />,
        },
        {
            value: 'SHARED',
            tooltip: 'Shared',
            el: <SharedBlend />,
        },
        {
            value: 'THEIRS',
            tooltip: 'Theirs',
            el: <PartnerAvatar />,
        },
    ];

    const selectedOption = options.find((o) => o.value === value);

    return (
        <FlexRow
            width={toPx(WIDTH)}
            height={toPx(HEIGHT)}
            borderRadius={toPx(HEIGHT)}
            px={toPx(PADDING_X)}
            py={toPx(PADDING_Y)}
            align="stretch"
            justify="space-around"
            position="relative"
            bg="gray.50"
            overflow="hidden"
            userSelect="none"
            {...props}
        >
            {options.map((o) => {
                const isDisabled = customOptions?.[o.value]?.disabled;
                const opacity = isDisabled
                    ? 0.3
                    : selectedOption?.value === o.value
                      ? 1
                      : 0.7;
                const hoverOpacity = isDisabled ? 0.3 : 1;
                const label = customOptions?.[o.value]?.label ?? o.tooltip;
                return (
                    <Tooltip key={o.value} placement="top" label={label}>
                        <FlexRow
                            flex={1}
                            justifyContent="center"
                            alignItems="center"
                            zIndex={10}
                            opacity={opacity}
                            {...hoverStyleProps({ opacity: hoverOpacity })}
                            onClick={() => {
                                if (isDisabled) return;
                                onChanged?.(o.value);
                            }}
                        >
                            {o.el}
                        </FlexRow>
                    </Tooltip>
                );
            })}
            <MotionBox
                position="absolute"
                left={0}
                width={toPx(OPTION_WIDTH)}
                height={toPx(PILL_HEIGHT)}
                border="1px solid"
                borderColor="gray.200"
                bg="white"
                zIndex={1}
                shadow="sm"
                borderRadius={toPx(HEIGHT)}
                variants={variants}
                animate={selectedOption?.value}
                // Known typing issue - no problem in operation.
                // Details: https://chakra-ui.com/getting-started/with-framer#chakra-factory
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                transition={transition}
            />
        </FlexRow>
    );
};

export default PartnerSwitcher;
